import { router, usePage } from "@inertiajs/react";
import { Building2, Check, ChevronsUpDown, Globe } from "lucide-react";
import { useState } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import type { Organization } from "@/types";

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function OrgAvatar({
  org,
  size = "sm",
}: {
  org: Organization;
  size?: "sm" | "lg";
}) {
  const sizeClass = size === "lg" ? "size-8" : "size-6";

  return (
    <Avatar className={`${sizeClass} rounded-md`}>
      {org.logo && <AvatarImage src={org.logo} alt={org.name} />}
      <AvatarFallback className="rounded-md text-xs">
        {getInitials(org.name)}
      </AvatarFallback>
    </Avatar>
  );
}

export function OrganizationSwitcher() {
  const { isMobile } = useSidebar();
  const { user, organization, organizations = [] } = usePage().props;
  const [switching, setSwitching] = useState(false);

  const isSuperadmin = user?.role === "superadmin";
  const canSwitch = organizations.length > 1 || isSuperadmin;

  const handleSwitch = (slug: string | null) => {
    if (switching) return;
    if (slug !== null && slug === organization?.slug) return;
    if (slug === null && !organization) return;

    setSwitching(true);
    router.post(
      "/app/session/switch_organization",
      { slug },
      {
        preserveState: false,
        onFinish: () => setSwitching(false),
      },
    );
  };

  if (!organization && !isSuperadmin) return null;

  if (!canSwitch && organization) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" className="cursor-default">
            <OrgAvatar org={organization} size="lg" />
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{organization.name}</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={switching}>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              {organization ? (
                <OrgAvatar org={organization} size="lg" />
              ) : (
                <Avatar className="size-8 rounded-md">
                  <AvatarFallback className="rounded-md text-xs">
                    {isSuperadmin ? <Globe className="size-4" /> : <Building2 className="size-4" />}
                  </AvatarFallback>
                </Avatar>
              )}
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {organization?.name ?? (isSuperadmin ? "Todas as organizações" : "Selecionar organização")}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              Organizações
            </DropdownMenuLabel>
            {isSuperadmin && (
              <>
                <DropdownMenuItem
                  onClick={() => handleSwitch(null)}
                  className="gap-2 p-2"
                  disabled={switching}
                >
                  <Avatar className="size-6 rounded-md">
                    <AvatarFallback className="rounded-md text-xs">
                      <Globe className="size-3" />
                    </AvatarFallback>
                  </Avatar>
                  <span className="flex-1 truncate">Todas as organizações</span>
                  {!organization && (
                    <Check className="text-muted-foreground ml-auto size-4" />
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {organizations.length > 0 ? (
              organizations.map((org) => (
                <DropdownMenuItem
                  key={org.id}
                  onClick={() => handleSwitch(org.slug)}
                  className="gap-2 p-2"
                  disabled={switching}
                >
                  <OrgAvatar org={org} />
                  <span className="flex-1 truncate">{org.name}</span>
                  {org.id === organization?.id && (
                    <Check className="text-muted-foreground ml-auto size-4" />
                  )}
                </DropdownMenuItem>
              ))
            ) : (
              <p className="text-muted-foreground px-2 py-1.5 text-xs text-center">
                Nenhuma organização encontrada
              </p>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
